import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TrustBadges from "@/components/TrustBadges";
import BannerCarousel from "@/components/BannerCarousel";
import ScrollTicker from "@/components/ScrollTicker";
import CategoryShowcase from "@/components/CategoryShowcase";
import FeaturedProducts from "@/components/FeaturedProducts";
import { useHomepageProducts } from "@/contexts/HomepageProductsContext";

interface Suggestion {
  type: "category" | "product";
  name: string;
  imageUrl?: string;
  id?: string;
}

export default function Catalog() {
  const navigate = useNavigate();
  const { categoryNames, featured, showcase } = useHomepageProducts();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { allCategories, allProducts } = useMemo(() => {
    const cats = new Set<string>(categoryNames);
    const prodsMap = new Map<string, { id: string; name: string; imageUrl: string }>();

    for (const p of featured) {
      prodsMap.set(p.id, { id: p.id, name: p.name ?? "", imageUrl: p.imageUrl ?? "" });
    }
    for (const [catName, p] of Object.entries(showcase)) {
      cats.add(catName);
      prodsMap.set(p.id, { id: p.id, name: p.name ?? "", imageUrl: p.imageUrl ?? "" });
    }

    return { allCategories: Array.from(cats).sort(), allProducts: Array.from(prodsMap.values()) };
  }, [categoryNames, featured, showcase]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalize = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const suggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const terms = searchQuery.split(/\s+/).filter(Boolean).map(normalize);
    const matchedCats = allCategories
      .filter((c) => terms.every((t) => normalize(c).includes(t)))
      .slice(0, 4)
      .map((name): Suggestion => ({ type: "category", name }));
    const matchedProds = allProducts
      .filter((p) => terms.every((t) => normalize(p.name).includes(t)))
      .slice(0, 6)
      .map((p): Suggestion => ({ type: "product", name: p.name, imageUrl: p.imageUrl, id: p.id }));
    return [...matchedCats, ...matchedProds];
  }, [searchQuery, allCategories, allProducts]);

  const handleCategorySelect = (category: string) => {
    navigate(`/catalogo?cat=${encodeURIComponent(category)}`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogo?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSuggestionClick = (s: Suggestion) => {
    setShowSuggestions(false);
    if (s.type === "category") {
      navigate(`/catalogo?cat=${encodeURIComponent(s.name)}`);
    } else {
      navigate(`/produto/${s.id}`);
    }
  };

  return (
    <div className="py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-site">
        <ScrollTicker />
        <BannerCarousel />

        {/* Search + CTA */}
        <div className="mb-8 px-4 sm:mb-12 sm:px-0">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-2 text-xl font-bold text-text-primary sm:text-2xl">
              Encontre o Brinde Perfeito
            </h2>
            <p className="mb-5 text-sm text-text-secondary sm:text-base">
              Pesquise Brindes por Categoria, Faixa de Preço ou Material que precisa
            </p>

            {/* Search com sugestões */}
            <div ref={wrapperRef} className="relative mb-5">
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="relative flex-1">
                  <svg className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Buscar canecas, canetas, cadernos, bambu..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                    className="w-full rounded-xl border border-border bg-bg py-3.5 pl-11 pr-4 text-sm text-text-primary placeholder-text-muted shadow-sm transition-shadow focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 sm:text-base"
                  />
                </div>
                <button
                  type="submit"
                  className="shrink-0 rounded-xl bg-accent px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-accent-hover hover:shadow-md active:scale-[0.98] sm:px-8 sm:text-base"
                >
                  Buscar
                </button>
              </form>

              {/* Dropdown de sugestões */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border border-border bg-bg shadow-lg">
                  {suggestions.some((s) => s.type === "category") && (
                    <div>
                      <div className="border-b border-border bg-surface px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                        Categorias
                      </div>
                      {suggestions.filter((s) => s.type === "category").map((s) => (
                        <button
                          key={`cat-${s.name}`}
                          onClick={() => handleSuggestionClick(s)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-text-primary hover:bg-surface transition-colors"
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                            </svg>
                          </span>
                          <span className="font-medium">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {suggestions.some((s) => s.type === "product") && (
                    <div>
                      <div className="border-b border-border bg-surface px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                        Produtos
                      </div>
                      {suggestions.filter((s) => s.type === "product").map((s) => (
                        <button
                          key={`prod-${s.id}`}
                          onClick={() => handleSuggestionClick(s)}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-text-primary hover:bg-surface transition-colors"
                        >
                          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-surface">
                            <img src={s.imageUrl} alt={s.name} className="h-full w-full object-contain" loading="lazy" />
                          </div>
                          <span className="truncate">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Botão Catálogo Completo — grande e chamativo */}
            <button
              onClick={() => navigate("/catalogo")}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-accent to-accent-hover px-8 py-4 text-base font-bold text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] sm:w-auto sm:text-lg"
            >
              <svg className="h-5 w-5 transition-transform group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016A3.001 3.001 0 0021 9.349" />
              </svg>
              Ver Catálogo Completo
              <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-0">
          <CategoryShowcase onSelect={handleCategorySelect} />
          <FeaturedProducts />
          <div className="mb-8 sm:mb-10">
            <TrustBadges />
          </div>
        </div>
      </div>
    </div>
  );
}
